// ═══════════════════════════════════════════════════════════════════
//  CursorsLayer — collaborative cursor dots on the Minimap.
// ═══════════════════════════════════════════════════════════════════
//
//  Renders one circle per peer using `activeCursors()` from
//  src/lib/minimapCursors.ts. Stale cursors fade and disappear per
//  the helper's TTL logic.
//
//  Pure presentation — the realtime subscription that produces
//  PeerCursor[] is the caller's responsibility (typically Supabase
//  Realtime in a hook).
//
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { activeCursors, type PeerCursor } from '@/lib/minimapCursors';

export interface CursorsLayerProps {
  peers: PeerCursor[];
  canvasWidth: number;
  canvasHeight: number;
  minimapWidth: number;
  minimapHeight: number;
  /** When omitted, defaults to 30s. */
  staleAfterMs?: number;
  /** When omitted, defaults to 5s. */
  fadeAfterMs?: number;
}

export function CursorsLayer({
  peers,
  canvasWidth,
  canvasHeight,
  minimapWidth,
  minimapHeight,
  staleAfterMs,
  fadeAfterMs,
}: CursorsLayerProps) {
  // Tick at 1Hz so cursor opacity fades smoothly without re-fetch.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (peers.length === 0) return;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [peers.length]);

  const projected = useMemo(
    () =>
      activeCursors(
        peers,
        { canvasWidth, canvasHeight, minimapWidth, minimapHeight },
        now,
        { staleAfterMs, fadeAfterMs },
      ),
    [peers, canvasWidth, canvasHeight, minimapWidth, minimapHeight, now, staleAfterMs, fadeAfterMs],
  );

  if (projected.length === 0) return null;

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={minimapWidth}
      height={minimapHeight}
      data-fw-minimap-cursors="true"
    >
      {projected.map((p) => (
        <g key={p.userId} opacity={p.opacity}>
          <circle
            cx={p.minimapX}
            cy={p.minimapY}
            r={3}
            fill={p.color}
            stroke="white"
            strokeWidth={1}
          />
          <title>{p.userName}</title>
        </g>
      ))}
    </svg>
  );
}
